import React, { useState, useEffect } from "react";
import {
  Modal,
  NumberInput,
  TextInput,
  InlineNotification,
  Tile,
  Form,
  FormGroup,
  Tag,
  ContentSwitcher,
  Switch,
} from "@carbon/react";
import "./topUpFuelModal.scss";
import { useTranslation } from "react-i18next";
import { queryClient } from "../..";
import { useAddClientDeposit } from "../../hooks/useAddClientDeposit";
import fuelIcon from "../../assets/icons/fuel.svg";
import { createCurrencyFormatter } from "../../utils/currencyFormatter";

export default function TopUpFuelModal({
  openModal,
  setOpenModal,
  editingClient,
  onSuccess,
}) {
  const { t, i18n } = useTranslation();
  const formatter = createCurrencyFormatter(i18n.language);
  const [balanceType, setBalanceType] = useState("fuel");
  const [fuel, setFuel] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorNotification, setErrorNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const fuelMutation = useAddClientDeposit();

  const handlePreventE = (evt) => {
    if (evt.key === "e") evt.preventDefault();
  };

  const onModalClose = () => {
    setFuel("");
    setSubmitting(false);
    setErrorNotification({
      visibility: false,
      title: "",
      subtitle: "",
      kind: "",
    });
    setOpenModal(false);
  };

  const cost = Number(editingClient?.cost);

  const isValueValid = fuel !== "" && !isNaN(Number(fuel)) && Number(fuel) > 0;

  // const fuelToSend =
  //   balanceType === "fuel"
  //     ? Math.ceil(Number(fuel) / 100) * 100
  //     : Math.ceil(Number(fuel) / cost / 100) * 100;

  const fuelToSend =
    balanceType === "fuel" ? Number(fuel) : Number(fuel) / cost;

  const fuelEquivalent = Math.round(Number(fuel) / cost); // целые

  const usdEquivalent = Math.round(Number(fuel) * cost * 10) / 10; // десятые

  // const showRoundingWarning =
  //   balanceType === "fuel" && isValueValid && Number(fuel) % 100 !== 0;

  const isSubmitDisabled = submitting || !isValueValid;

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isValueValid || submitting) return;

    setSubmitting(true);

    fuelMutation.mutate(
      {
        guid: editingClient.guid,
        fuel: fuelToSend,
      },
      {
        onSuccess: () => {
          onSuccess?.();
          onModalClose();
          queryClient.invalidateQueries({ queryKey: ["getInfo"] });
          queryClient.invalidateQueries({ queryKey: ["chat_balance_history"] });
        },
        onError: () => {
          setErrorNotification({
            visibility: true,
            title: "authentication.notification.requestError500.title",
            subtitle: "authentication.notification.requestError500.serverError",
            kind: "error",
          });
          setSubmitting(false);
        },
      },
    );
  };

  return (
    <Modal
      open={!!openModal}
      onRequestClose={onModalClose}
      modalHeading={`${t("main.admin.top_up_fuel.modal.header")}`}
      modalLabel={`${t("main.chat.depositModal.label")}`}
      primaryButtonText={`${t("main.admin.top_up_fuel.modal.add")}`}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      primaryButtonDisabled={isSubmitDisabled}
      onRequestSubmit={handleSubmit}
      onSecondarySubmit={onModalClose}
      closeButtonLabel={`${t("main.modalText.close")}`}
      className="top_up_fuel_modal"
    >
      <Tile className="top_up_fuel_modal_tile">
        <div className="top_up_fuel_modal_tile_client-info">
          <span>{`${t("main.admin.top_up_fuel.modal.title", { client: editingClient?.client })}`}</span>
          <span>{editingClient?.guid}</span>
        </div>
        <div className="top_up_fuel_modal_tile_balance-info">
          <span className="top_up_fuel_modal_tile_balance-info_balance">
            <img src={fuelIcon} alt="Fuel Icon" className="fuel_icon" />
            {editingClient?.fuel_balance}
          </span>
          <Tag size="lg" type="high-contrast">
            {`${t("main.admin.top_up_fuel.modal.costDesc", {
              cost: Number(
                (Number(editingClient?.cost) * 100).toPrecision(12),
              ).toString(),
            })}`}
          </Tag>
        </div>
      </Tile>

      <ContentSwitcher
        selectedIndex={balanceType === "fuel" ? 0 : 1}
        onChange={({ name }) => setBalanceType(name)}
        lowContrast
        className="top_up_fuel_modal_balance_switcher"
      >
        <Switch
          name="fuel"
          text={t("main.chat.chatClientsTable.balanceFuel")}
        />
        <Switch
          name="usd"
          disabled={!editingClient?.cost}
          text={t("main.chat.chatClientsTable.balanceUSD")}
        />
      </ContentSwitcher>

      <Form
        aria-label="sample form"
        className="top_up_fuel_modal_form"
        onSubmit={handleSubmit}
      >
        <FormGroup legendText="" className="top_up_fuel_modal_form_group">
          <NumberInput
            id="fuel"
            label={
              balanceType === "fuel"
                ? t("main.admin.top_up_fuel.modal.addFuel")
                : t("main.admin.top_up_fuel.modal.addBalance")
            }
            value={fuel}
            min={0}
            max={1000000}
            // step={balanceType === "fuel" ? 100 : 1}
            onChange={(evt, { value }) => setFuel(value)}
            onKeyDown={handlePreventE}
            allowEmpty
            hideSteppers
            invalid={isNaN(Number(fuel)) || Number(fuel) < 0}
            invalidText={t("main.chat.addModal.validationText.invalidValue")}
            // warn={showRoundingWarning}
            // warnText={t("main.admin.top_up_fuel.modal.roundingWarning", {
            //   value: fuelToSend,
            // })}
          />
          <div>
            {balanceType === "fuel" ? (
              <>
                {t("main.admin.top_up_fuel.modal.equivalentUsd", {
                  value: formatter.format(
                    Math.floor(usdEquivalent * 100) / 100,
                  ),
                })}
              </>
            ) : (
              <>
                {t("main.admin.top_up_fuel.modal.equivalentFuel", {
                  value: fuelEquivalent,
                })}{" "}
              </>
            )}
          </div>
        </FormGroup>
      </Form>
      {!editingClient?.cost && (
        <InlineNotification
          aria-label="notification"
          kind="warning"
          hideCloseButton
          onClose={() => {}}
          statusIconDescription="notification"
          subtitle={t("main.admin.top_up_fuel.modal.costNotificationSubtitle")}
          title={t("main.admin.top_up_fuel.modal.costNotificationTitle")}
        />
      )}

      {errorNotification.visibility && (
        <InlineNotification
          title={t(errorNotification.title)}
          subtitle={t(errorNotification.subtitle)}
          kind={errorNotification.kind}
          onClose={() =>
            setErrorNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
